"use client";

import { useEffect } from "react";
import { CreaseField } from "@/components/CreaseField";
import { SheetButton, SyncIcon } from "@/components/SheetControl";
import { martianMono, saira } from "./fonts";
/* THE STYLESHEET HAS TO BE IMPORTED HERE. `global-error` REPLACES
 * app/layout.tsx when it fires — it renders its own <html> and <body>, so the
 * layout never runs and none of its <link> tags ship with this page. Without
 * this line every class below resolves to nothing and the worst screen in the
 * app renders as unstyled Times New Roman. */
import "./globals.css";

/* THE LAYOUT'S TWO FACES, IMPORTED. app/layout.tsx puts these two
 * `.variable` class names on <html> so globals.css's --font-body / --font-mono
 * resolve to Saira and Martian Mono. This file renders its own <html>, so it
 * hangs the same class names on its own tag, from the same `./fonts`
 * declarations, or the heading and the crease IDs silently fall back to a
 * system face while the rest of the app looks right. */

/**
 * THE ROOT-LAYOUT ERROR BOUNDARY. It fires when app/layout.tsx itself throws,
 * or when a throw anywhere else has no error.tsx above it to land in. There is
 * no nav here, no patient, no wordmark link — the layout that carries all of
 * that is the thing that has been replaced. That makes this page the login
 * screen's sibling rather than a record page's, and app/globals.css builds it
 * the same way: the mark on a plain cell, a sentence, one control, over the
 * crease field the record's own pages wear.
 *
 * Someone meets this mid-way through reading their own medical records, so the
 * register is the app's ordinary one and not alarm chrome — no red, no hazard
 * band, no digest in the reader's face. It says which app this is, that
 * nothing in the record moved, and offers the one action that usually works.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    void (async () => {
      try {
        const { reportError } = await import("@/lib/flare");
        await reportError(error, { kind: "client", digest: error.digest });
      } catch {
        // The page is already broken; a failed report must not make it worse.
      }
    })();
  }, [error]);

  return (
    <html lang="en" className={`${saira.variable} ${martianMono.variable}`}>
      <head>
        {/* Next's global-error path bypasses `metadata`/`viewport` resolution
            entirely — its own DefaultGlobalError has the same gap — so without
            this the installed iOS PWA renders this page at desktop width.
            Which is precisely the case this file exists to cover. */}
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
      </head>
      <body>
        <main className="boundary-page">
          <CreaseField />

          {/* app/icon.svg, drawn from the tokens — the same object as the
              login mark and the browser tab's. On a plain cell rather than a
              packet for the login page's measured reason: the lit plane is
              foil, and foil on foil loses the fold that is the whole mark. */}
          <div className="sheet-cell boundary-mark" aria-hidden>
            <svg width="52" height="52" viewBox="0 0 100 100">
              <path d="M5 10 L30 10 L50 60 L50 94 Z" fill="var(--accent)" />
              <path d="M95 10 L70 10 L50 60 L50 94 Z" fill="var(--ink)" />
            </svg>
          </div>

          <div className="boundary-copy">
            <h1 className="dashboard-title">Vitals couldn&apos;t show this page</h1>
            <p className="page-lead">
              Nothing in the record has changed. Trying again usually clears it.
            </p>
          </div>

          {/* The sheet's parallelogram control, from the one component that
              writes it. Its two halves each carry the app's 44px one-handed
              floor, and the cap's icon is aria-hidden, so the accessible name
              is exactly "Try again". The cap's step down off foil is scoped to
              `.boundary-page` in globals.css, so it needs no class here. */}
          <SheetButton
            label="Try again"
            cap={<SyncIcon />}
            onClick={() => reset()}
          />
        </main>
      </body>
    </html>
  );
}
